/**
 * Parsers for structured text fields in innovation-specific Excel columns.
 * Follows the pattern established by partnershipParsers.js.
 */

/**
 * Parse Actors Dev/Use Step 1 (Col 57).
 * Format: "• Actor: Farmers/Pastoralists; Sex and age disaggregation: Yes; How many: 22162; Women: 1291; Women Youth: 0; Men: 20871; Men Youth: 0 • Actor: ..."
 * Returns: [{ actorType, total, women, womenYouth, men, menYouth }]
 */
export function parseActorsDevUse(str) {
  if (!str || typeof str !== 'string') return [];

  const actors = [];
  const entries = str.split('•').map(s => s.trim()).filter(Boolean);

  for (const entry of entries) {
    const actorMatch = entry.match(/Actor:\s*([^;]+)/i);
    const totalMatch = entry.match(/How many:\s*([\d,]+)/i);
    const womenYouthMatch = entry.match(/Women Youth:\s*([\d,]+)/i);
    const womenMatch = entry.match(/\bWomen:\s*([\d,]+)/i);
    const menYouthMatch = entry.match(/Men Youth:\s*([\d,]+)/i);
    const menMatch = entry.match(/\bMen:\s*([\d,]+)/i);

    if (actorMatch) {
      actors.push({
        actorType: actorMatch[1].trim(),
        total: totalMatch ? parseInt(totalMatch[1].replace(/,/g, ''), 10) : 0,
        women: womenMatch ? parseInt(womenMatch[1].replace(/,/g, ''), 10) : 0,
        womenYouth: womenYouthMatch ? parseInt(womenYouthMatch[1].replace(/,/g, ''), 10) : 0,
        men: menMatch ? parseInt(menMatch[1].replace(/,/g, ''), 10) : 0,
        menYouth: menYouthMatch ? parseInt(menYouthMatch[1].replace(/,/g, ''), 10) : 0,
      });
    }
  }

  return actors;
}

/**
 * Parse IPSR Actors (Col 67).
 * Format: "Farmers/Pastoralists | How Many: 186000 | Women: 93000 | Women Youth: 0 | Men: 93000 | Men Youth: 0"
 * Multiple actors separated by newlines.
 * Returns: [{ actorType, total, women, womenYouth, men, menYouth }]
 */
export function parseIPSRActors(str) {
  if (!str || typeof str !== 'string') return [];

  const actors = [];
  const blocks = str.split(/\r?\n/).map(s => s.trim()).filter(Boolean);

  for (const block of blocks) {
    const parts = block.split('|').map(s => s.trim()).filter(Boolean);
    if (parts.length === 0) continue;

    // First part is the actor type (should not contain "How Many")
    const actorType = parts[0];
    if (/How Many/i.test(actorType)) continue;

    const totalMatch = block.match(/How Many:\s*([\d,]+)/i);
    const womenYouthMatch = block.match(/Women Youth:\s*([\d,]+)/i);
    const womenMatch = block.match(/\bWomen:\s*([\d,]+)/i);
    const menYouthMatch = block.match(/Men Youth:\s*([\d,]+)/i);
    const menMatch = block.match(/\bMen:\s*([\d,]+)/i);

    actors.push({
      actorType: actorType.trim(),
      total: totalMatch ? parseInt(totalMatch[1].replace(/,/g, ''), 10) : 0,
      women: womenMatch ? parseInt(womenMatch[1].replace(/,/g, ''), 10) : 0,
      womenYouth: womenYouthMatch ? parseInt(womenYouthMatch[1].replace(/,/g, ''), 10) : 0,
      men: menMatch ? parseInt(menMatch[1].replace(/,/g, ''), 10) : 0,
      menYouth: menYouthMatch ? parseInt(menYouthMatch[1].replace(/,/g, ''), 10) : 0,
    });
  }

  return actors;
}

/**
 * Parse Inno Use Actors Information (Col 73).
 * Format: "Actor Type: Farmers; How Many: 22162; Women: 1291; Women Youth: 0; Men: 20871; Men Youth: 0"
 * Multiple actors separated by newlines.
 * Returns: [{ actorType, total, women, womenYouth, men, menYouth }]
 */
export function parseInnoUseActors(str) {
  if (!str || typeof str !== 'string') return [];

  const actors = [];
  const lines = str.split(/\r?\n/).map(s => s.trim()).filter(Boolean);

  for (const line of lines) {
    const typeMatch = line.match(/Actor Type:\s*([^;]+)/i);
    const totalMatch = line.match(/How Many:\s*([\d,]+)/i);
    const womenYouthMatch = line.match(/Women Youth:\s*([\d,]+)/i);
    const womenMatch = line.match(/\bWomen:\s*([\d,]+)/i);
    const menYouthMatch = line.match(/Men Youth:\s*([\d,]+)/i);
    const menMatch = line.match(/\bMen:\s*([\d,]+)/i);

    if (typeMatch) {
      actors.push({
        actorType: typeMatch[1].trim(),
        total: totalMatch ? parseInt(totalMatch[1].replace(/,/g, ''), 10) : 0,
        women: womenMatch ? parseInt(womenMatch[1].replace(/,/g, ''), 10) : 0,
        womenYouth: womenYouthMatch ? parseInt(womenYouthMatch[1].replace(/,/g, ''), 10) : 0,
        men: menMatch ? parseInt(menMatch[1].replace(/,/g, ''), 10) : 0,
        menYouth: menYouthMatch ? parseInt(menYouthMatch[1].replace(/,/g, ''), 10) : 0,
      });
    }
  }

  return actors;
}

/**
 * Parse IPSR Core Innovation Package Assessment Info (Col 65).
 * Extracts result code, title, readiness level, and use level.
 * Returns: { resultCode, title, readinessLevel, useLevel } | null
 */
export function parseIPSRCoreInfo(str) {
  if (!str || typeof str !== 'string') return null;

  const codeMatch = str.match(/(?:Result Code|Code):\s*([^;\n|]+)/i);
  const titleMatch = str.match(/Title:\s*([^;\n|]+)/i);
  const readinessMatch = str.match(/Readiness\s*(?:Level)?:\s*(\d+)/i);
  const useMatch = str.match(/Use\s*(?:Level)?:\s*(\d+)/i);

  if (!codeMatch && !readinessMatch && !titleMatch) return null;

  return {
    resultCode: codeMatch ? codeMatch[1].trim() : '',
    title: titleMatch ? titleMatch[1].trim() : '',
    readinessLevel: readinessMatch ? parseInt(readinessMatch[1], 10) : null,
    useLevel: useMatch ? parseInt(useMatch[1], 10) : null,
  };
}

/**
 * Parse IPSR Complementary Innovation Package Assessment Info (Col 66).
 * Multiple complementary innovations per row separated by bullets or newlines.
 * Returns: [{ resultCode, title, readinessLevel, useLevel }]
 */
export function parseIPSRComplementaryInfo(str) {
  if (!str || typeof str !== 'string') return [];

  const results = [];
  // Actual format: newline-separated entries, each starting with " - Result Code: NNN ..."
  const lines = str.split(/\r?\n/).map(s => s.trim()).filter(Boolean);

  for (const line of lines) {
    const codeMatch = line.match(/Result Code:\s*(\d+)/i);
    const titleMatch = line.match(/Result Title:\s*(.+?)(?:\s*-\s*(?:readiness|use)\s|$)/i);
    const readinessMatch = line.match(/readiness level:\s*(\d+)/i);
    const useMatch = line.match(/use level:\s*(\d+)/i);

    if (codeMatch || readinessMatch || useMatch) {
      results.push({
        resultCode: codeMatch ? codeMatch[1] : '',
        title: titleMatch ? titleMatch[1].trim() : '',
        readinessLevel: readinessMatch ? parseInt(readinessMatch[1], 10) : null,
        useLevel: useMatch ? parseInt(useMatch[1], 10) : null,
      });
    }
  }

  return results;
}

/**
 * Parse IPSR Organizations (Col 68).
 * Format: "Government (National) How Many: 2 /n Private Sector How Many: 5"
 * Returns: [{ orgType, count }]
 */
export function parseIPSROrganizations(str) {
  if (!str || typeof str !== 'string') return [];

  const orgs = [];
  const entries = str.split(/\s*\/n\s*|\r?\n/).map(s => s.trim()).filter(Boolean);

  for (const entry of entries) {
    const match = entry.match(/^(.+?)\s*(?:How Many|Count):\s*([\d,]+)/i);
    if (match) {
      orgs.push({
        orgType: match[1].trim(),
        count: parseInt(match[2].replace(/,/g, ''), 10),
      });
    }
  }

  return orgs;
}
